/**
 * Live prices for structured data, readable during SSR / prerender.
 *
 * Reads the configured course trial price for the optional intro offer.
 * Assessment access belongs to membership and has no standalone Offer.
 */
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface SchemaPrices {
  /** Configured landing-trial course price, or null when unknown. */
  trialPrice: number | null;
}

function publicClient() {
  const url = process.env["SUPABASE_URL"] || import.meta.env["VITE_SUPABASE_URL"];
  const key =
    process.env["SUPABASE_PUBLISHABLE_KEY"] || import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) return null;

  return createClient<Database>(url, key, {
    auth: { persistSession: false },
    global: {
      // sb_ publishable keys are opaque, not JWTs — send them as `apikey` only.
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });
}

const num = (v: unknown): number | null => {
  const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const getSchemaPrices = createServerFn({ method: "GET" }).handler(
  async (): Promise<SchemaPrices> => {
    const supabasePublic = publicClient();
    if (!supabasePublic) return { trialPrice: null };

    const course = await supabasePublic
        .from("courses")
        .select("trial_price")
        .eq("is_landing_trial", true)
        .is("deleted_at", null)
        .maybeSingle();

    return {
      trialPrice: num(course.data?.trial_price),
    };
  },
);
